import { BetsQueryKeys } from '@/api/bets/queryKey';
import { useUserAccount } from '@/api/user/queries';
import { DetailedBet } from '@/models/detailed-bet';
import { GenericError } from '@/models/generic-error';
import { BetsSortAndFilterQuery } from '@/services/dto/bet/me/bets-sort-and-filter-query.query';
import {
  useInfiniteQueryCallbacks,
  UseInfiniteQueryReturnProps,
} from '@starsoft/common/hooks';
import { PaginatedResponse } from '@starsoft/common/models';
import { InfiniteData, useInfiniteQuery } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { useMemo } from 'react';
import { UseBetsMeInfinityQueryKeyProps } from '../props';
import { fetchBetsMe } from './service';

export function useBetsMeInfinity(options?: BetsSortAndFilterQuery) {
  const { isAuthorized } = useUserAccount();

  const filter: BetsSortAndFilterQuery = {
    sortOrder: options?.sortOrder,
    aggregatorId: options?.aggregatorId,
    gameId: options?.gameId,
    limit: options?.limit ?? 15,
  };
  const queryKey: UseBetsMeInfinityQueryKeyProps = [
    BetsQueryKeys.MeInfinity,
    filter,
  ];

  const { getNextPageParam, getPreviousPageParam } =
    useInfiniteQueryCallbacks();

  const { data, isPending, fetchStatus, error, refetch, isFetching, ...rest } =
    useInfiniteQuery<
      PaginatedResponse<DetailedBet>,
      AxiosError<GenericError>,
      InfiniteData<PaginatedResponse<DetailedBet>>,
      UseBetsMeInfinityQueryKeyProps
    >({
      queryFn: fetchBetsMe,
      queryKey,
      staleTime: 5 * 1000,
      structuralSharing: false,
      //@ts-ignore
      enabled: isAuthorized && options?.enabled && options?.type === 'infinity',
      refetchOnWindowFocus: false,
      getNextPageParam,
      getPreviousPageParam,
      initialPageParam: 1,
    });

  const bets = useMemo(() => data?.pages.flatMap(page => page.data), [data]);
  const metadata = useMemo(
    () => data?.pages.flatMap(page => page.metadata)?.[0],
    [data],
  );

  return {
    isPending: (isPending || isFetching) && fetchStatus !== 'idle',
    error,
    refetch,
    data: bets,
    fetchStatus,
    bets,
    isFetching,
    metadata,
    ...rest,
  } as unknown as UseInfiniteQueryReturnProps<DetailedBet[]>;
}
